import React, { useContext, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Typography,
} from "@mui/material";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import ForwardIcon from "@mui/icons-material/Forward";
import {
  CheckBoxIcon,
  CheckedBoxIcon,
  SkipVideoMessageModal,
} from "components";
import { ArrowBack } from "assests/images";
import { IAgreementForm } from "utility/interfaces";
import { SuccessErrorModalDispatchContext } from "contexts/success-error-context";
import { openSucessErrorModal } from "utility/helpers";

type ComponentProps = {
  videoSrc: string;
  videoSrcMobile: string;
  videoElement: React.RefObject<HTMLVideoElement>;
  currentWindowWidth: number;
  handleCancelAgreementModalOpen: any;
  handleClickAgree: any;
};

export const MultistepAgreementForm = (props: ComponentProps) => {
  //extract props
  const {
    videoSrc,
    videoSrcMobile,
    videoElement,
    currentWindowWidth,
    handleCancelAgreementModalOpen,
    handleClickAgree,
  } = props;

  // context variables
  const setSuccessErrorContext = useContext(SuccessErrorModalDispatchContext);

  // State variables
  const [currentStep, setCurrentStep] = useState(1);
  const [isVideoEnded, setIsVideoEnded] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isSkipeMessageModalOpen, setIsSkipMessageModalOpen] = useState(false);
  const [formValues, setFormValues] = useState<IAgreementForm>({
    NoInsuranceParticipation: false,
    SupportsCharitableMission: false,
    TransparentPostCarePricing: false,
    AcknowledgmentAndConsent: false,
  });

  // handle events and functions
  const handleClickPlay = () => {
    videoElement.current?.play();
    setIsVideoPlaying(true);
  };

  const handleVideoPlay = () => {
    setIsVideoPlaying(true);
  };
  const handleVideoPause = () => {
    setIsVideoPlaying(false);
  };

  const videoEnded = () => {
    setIsVideoEnded(true);
  };

  const handleClickNext = () => {
    if (isSkipeMessageModalOpen) setIsSkipMessageModalOpen(false);
    let isError = false;

    if (currentStep === 2 && formValues.NoInsuranceParticipation === false) {
      isError = true;
    } else if (
      currentStep === 3 &&
      formValues.SupportsCharitableMission === false
    ) {
      isError = true;
    } else if (
      currentStep == 4 &&
      formValues.TransparentPostCarePricing === false
    ) {
      isError = true;
    }

    if (isError) {
      openSucessErrorModal(
        setSuccessErrorContext,
        "Error",
        "Please check the box to proceed further.",
        false
      );
      return;
    } else {
      setCurrentStep((prevStep) => prevStep + 1);
    }
  };

  const handleClickBack = () => {
    if (currentStep - 1 === 1) {
      setFormValues({
        NoInsuranceParticipation: false,
        SupportsCharitableMission: false,
        TransparentPostCarePricing: false,
        AcknowledgmentAndConsent: false,
      });
    }
    setCurrentStep((prevStep) => (prevStep > 1 ? prevStep - 1 : prevStep));
  };

  const handleFormValueChange = (name: string, value: any) => {
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
  };

  const handleSubmit = () => {
    const allCheckBoxValues = Object.values(formValues);

    if (allCheckBoxValues.includes(false)) {
      openSucessErrorModal(
        setSuccessErrorContext,
        "Error",
        "Please check the box to proceed further.",
        false
      );
      return;
    }
    handleClickAgree();
  };

  return (
    <Box className="request-container-box">
      {currentStep == 1 && (
        <>
          {videoSrc != "" && currentWindowWidth > 767 && (
            <Box className="video-container" justifyContent={"center"}>
              <video
                id="video"
                ref={videoElement}
                controls
                controlsList="nodownload nofullscreen noplaybackrate"
                src={videoSrc}
                onEnded={videoEnded}
                onPlay={handleVideoPlay}
                onPause={handleVideoPause}
                onClick={(e) => {
                  if (e.currentTarget.paused) e.currentTarget.play();
                  else e.currentTarget.pause();
                }}
                onFocus={(e) => e.target.blur()}
                disablePictureInPicture
              ></video>
              {!isVideoPlaying && (
                <button id="playButton" onClick={handleClickPlay}>
                  <PlayCircleIcon />
                  <span>Play</span>
                </button>
              )}
            </Box>
          )}

          {videoSrcMobile != "" && currentWindowWidth <= 767 && (
            <Box className="video-container-mobile" justifyContent={"center"}>
              <video
                id="video"
                ref={videoElement}
                controls
                controlsList="nodownload nofullscreen noplaybackrate"
                src={videoSrcMobile}
                onEnded={videoEnded}
                onPlay={handleVideoPlay}
                onPause={handleVideoPause}
                onClick={(e) => {
                  if (e.currentTarget.paused) e.currentTarget.play();
                  else e.currentTarget.pause();
                }}
                onFocus={(e) => e.target.blur()}
                disablePictureInPicture
              ></video>
              {!isVideoPlaying && (
                <button id="playButton" onClick={handleClickPlay}>
                  <PlayCircleIcon />
                  <span>Play</span>
                </button>
              )}
            </Box>
          )}
          <Box className="agreement-buttons">
            <Button
              // disabled={!isVideoEnded}
              className={`large-custom success-btn ${
                !isVideoEnded ? "Mui-disabled" : ""
              }`}
              variant="contained"
              color="success"
              size="large"
              onClick={() => {
                if (isVideoEnded) {
                  handleClickNext();
                } else {
                  if (videoElement.current) videoElement.current.pause();
                  setIsSkipMessageModalOpen(true);
                }
              }}
              style={{ pointerEvents: "all" }}
            >
              Next
            </Button>
            <Button
              className="large-custom"
              variant="contained"
              color="error"
              size="large"
              onClick={handleCancelAgreementModalOpen}
            >
              Cancel
            </Button>
          </Box>
        </>
      )}
      {currentStep >= 2 && currentStep <= 6 && (
        <>
          <div className="request-header">
            <div></div>
            <Button onClick={handleClickBack} variant="outlined">
              <img src={ArrowBack} alt="arrow" />
              Back
            </Button>
          </div>
          <Box className="request-box">
            <Box padding={"30px 10px"}>
              <Typography variant="h1" align="center">
                Doctors to You - Patient Agreement Form
              </Typography>
              <Box>
                <Typography variant="h4">
                  Welcome to Doctors to You (DTY). Please review and check each
                  section to acknowledge your agreement with our terms of
                  service before we begin care.
                </Typography>
                <hr />
                <FormControl
                  component="fieldset"
                  className="radio-inputs agreement-form-text"
                >
                  {currentStep === 2 && (
                    <>
                      <Typography variant="h4">
                        1. No Insurance Participation
                      </Typography>
                      <Typography variant="body1">
                        DTY is a concierge medical service that does not accept
                        insurance of any kind. There are no insurance cards,
                        co-pays, or insurance paperwork involved.
                      </Typography>
                      <Box
                        className="checkbox-highlight"
                        display="flex"
                        alignItems="center"
                        gap={2}
                      >
                        <ForwardIcon className="pointer-icon" />
                        <FormControlLabel
                          control={
                            <Checkbox
                              disableFocusRipple
                              disableRipple
                              icon={<CheckBoxIcon />}
                              checked={formValues.NoInsuranceParticipation}
                              onChange={() =>
                                handleFormValueChange(
                                  "NoInsuranceParticipation",
                                  !formValues.NoInsuranceParticipation
                                )
                              }
                              checkedIcon={<CheckedBoxIcon />}
                            />
                          }
                          label="I understand that DTY does not accept or bill any health insurance, and all care is provided outside of the insurance system."
                        />
                      </Box>
                    </>
                  )}

                  {currentStep === 3 && (
                    <>
                      <Typography variant="h4">
                        2. Supports a Charitable Mission
                      </Typography>
                      <Typography variant="body1">
                        All proceeds from your care support Humans for
                        Healthcare, a registered 501(c)(3) nonprofit
                        organization based in Washington, DC.
                      </Typography>
                      <Box
                        className="checkbox-highlight"
                        display="flex"
                        alignItems="center"
                        gap={2}
                      >
                        <ForwardIcon className="pointer-icon" />
                        <FormControlLabel
                          control={
                            <Checkbox
                              disableFocusRipple
                              disableRipple
                              icon={<CheckBoxIcon />}
                              checked={formValues.SupportsCharitableMission}
                              onChange={() =>
                                handleFormValueChange(
                                  "SupportsCharitableMission",
                                  !formValues.SupportsCharitableMission
                                )
                              }
                              checkedIcon={<CheckedBoxIcon />}
                            />
                          }
                          label="I understand that the fees I pay support the charitable and educational programs of Humans for Healthcare, a 501(c)(3) nonprofit."
                        />
                      </Box>
                    </>
                  )}

                  {currentStep === 4 && (
                    <>
                      <Typography variant="h4">
                        3. Transparent, Post-Care Pricing
                      </Typography>
                      <Typography variant="body1">
                        Final pricing is determined after all care is complete,
                        ensuring follow-up and full recovery support. Initial
                        fees are:
                      </Typography>
                      <ul style={{ listStyleType: "disc", marginLeft: "25px" }}>
                        <li>
                          <Typography variant="body1">
                            $1,000 for a house call
                          </Typography>
                        </li>
                        <li>
                          <Typography variant="body1">
                            $500 for a virtual consultation
                          </Typography>
                        </li>
                      </ul>
                      <Typography variant="body1">
                        Additional charges may apply for: time, supplies,
                        medications, labs, testing, referrals, after-hours or
                        holiday surcharges.
                      </Typography>
                      <Box
                        className="checkbox-highlight"
                        display="flex"
                        alignItems="center"
                        gap={2}
                      >
                        <ForwardIcon className="pointer-icon" />
                        <FormControlLabel
                          control={
                            <Checkbox
                              disableFocusRipple
                              disableRipple
                              icon={<CheckBoxIcon />}
                              checked={formValues.TransparentPostCarePricing}
                              onChange={() =>
                                handleFormValueChange(
                                  "TransparentPostCarePricing",
                                  !formValues.TransparentPostCarePricing
                                )
                              }
                              checkedIcon={<CheckedBoxIcon />}
                            />
                          }
                          label="I understand that initial costs start at $500-$1,000, and final costs are determined after all care is completed, including any follow-up or additional services."
                        />
                      </Box>
                    </>
                  )}

                  {currentStep === 5 && (
                    <>
                      <Typography variant="h4">
                        4. Acknowledgment and Consent
                      </Typography>
                      <Typography variant="body1">
                        DTY is a premium, personalized medical service designed
                        for those who value top-tier, trust-based care. If you
                        agree to these terms, care begins immediately.
                      </Typography>
                      <Box
                        className="checkbox-highlight"
                        display="flex"
                        alignItems="center"
                        gap={2}
                      >
                        <ForwardIcon className="pointer-icon" />
                        <FormControlLabel
                          control={
                            <Checkbox
                              disableFocusRipple
                              disableRipple
                              icon={<CheckBoxIcon />}
                              checked={formValues.AcknowledgmentAndConsent}
                              onChange={() =>
                                handleFormValueChange(
                                  "AcknowledgmentAndConsent",
                                  !formValues.AcknowledgmentAndConsent
                                )
                              }
                              checkedIcon={<CheckedBoxIcon />}
                            />
                          }
                          label="I have read and agree to all terms above and wish to proceed with care from DTY."
                        />
                      </Box>
                    </>
                  )}
                </FormControl>
              </Box>

              <div className="request-btn-group">
                {currentStep === 5 ? (
                  <Button
                    variant="contained"
                    color="success"
                    size="large"
                    onClick={handleSubmit}
                  >
                    I Agree
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="success"
                    size="large"
                    onClick={handleClickNext}
                  >
                    Next
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="error"
                  size="large"
                  onClick={handleCancelAgreementModalOpen}
                >
                  Cancel
                </Button>
              </div>
            </Box>
          </Box>
        </>
      )}

      {/* Modal Section */}
      <SkipVideoMessageModal
        isModalOpen={isSkipeMessageModalOpen}
        handleModalClose={() => setIsSkipMessageModalOpen(false)}
        handleSkipClick={handleClickNext}
      />
    </Box>
  );
};
